/**
 * POM-6223 — Non-Routable Orders holding queue.
 *
 * EVERY ROW IN THIS QUEUE IS A PRESCRIPTION THAT IS NOT BEING FILLED. Parking writes no
 * rx_request, rx_order, rx_request_line_item or prescription row and notifies no partner, so this
 * screen is the only place anyone at PerfectRx can see that the request exists and the only route
 * by which a human acts on it.
 */

// NonRoutableQueueStatus — the statuses the queue read can serve.
//
// THE FIRST TWO ARE THE DEFAULT VIEW AND CANCELLED IS NOT. Asking for no status at all still
// serves only the two ACTIVE ones, which are exactly the two the partial index
// idx_non_routable_request_queue covers; CANCELLED is reachable only by an operator who ticks it
// in the Status filter. That split lives in the backend (NON_ROUTABLE_DEFAULT_STATUSES vs
// NON_ROUTABLE_FILTERABLE_STATUSES in nonRoutableWorkQueueService.ts) and is repeated here only
// as labels — do not reimplement the defaulting in the portal.
export const NON_ROUTABLE_QUEUE_STATUS = {
  PENDING: 'PENDING',
  RESOLUTION_IN_PROGRESS: 'RESOLUTION_IN_PROGRESS',
  CANCELLED: 'CANCELLED',
};

// NonRoutableRequestStatus — the full lifecycle, served by the by-id read and by both mutations.
// There is no enum object for it: nothing compares against RESOLVED or CANCELLED by name, and the
// labels below are the only place the terminal statuses are needed.
export const NON_ROUTABLE_STATUS_LABEL = {
  PENDING: 'Pending',
  RESOLUTION_IN_PROGRESS: 'Resolution In Progress',
  RESOLVED: 'Resolved',
  CANCELLED: 'Cancelled',
};

export const NON_ROUTABLE_STATUS_COLOR = {
  PENDING: 'warning',
  RESOLUTION_IN_PROGRESS: 'info',
  RESOLVED: 'success',
  CANCELLED: 'default',
};

// Exactly the statuses the schema accepts on the filter, and nothing else — offering a status the
// enum refuses would build a control that can only produce an error.
//
// RESOLVED IS ABSENT ON PURPOSE AND IS NOT AN OVERSIGHT. A resolved request became a real POM
// order and is visible in the ordinary order queues, so listing it here would be a second, worse
// view of something already shown properly elsewhere. A CANCELLED request has no downstream
// artifact anywhere — parking wrote no rx_request, rx_order or prescription row and cancelling
// creates none — so this screen is the ONLY place it can ever be seen, which is why it is worth
// admitting a terminal status here at all.
export const NON_ROUTABLE_STATUS_OPTIONS = [
  { label: NON_ROUTABLE_STATUS_LABEL.PENDING, value: NON_ROUTABLE_QUEUE_STATUS.PENDING },
  {
    label: NON_ROUTABLE_STATUS_LABEL.RESOLUTION_IN_PROGRESS,
    value: NON_ROUTABLE_QUEUE_STATUS.RESOLUTION_IN_PROGRESS,
  },
  { label: NON_ROUTABLE_STATUS_LABEL.CANCELLED, value: NON_ROUTABLE_QUEUE_STATUS.CANCELLED },
];

/**
 * EVERY LABEL MAP BELOW FALLS BACK TO THE RAW VALUE — read them through `labelOf`, never with a
 * bare index. The backend serves several of these as plain Strings precisely so an unexpected
 * value cannot take the page to data:null (relayErrorCode), and the enums grow with every story
 * (POM-6279 added three reasons, a source domain and two resolution types). A value this build
 * has no label for must still render as itself, never as a blank cell.
 */
export const labelOf = (labels, value) => labels?.[value] ?? value;

// NonRoutableParkReason (typeDefs `enum NonRoutableParkReason`).
export const NON_ROUTABLE_REASON_LABEL = {
  STATE_NOT_LICENSED: 'No licensed pharmacy for the ship-to state',
  // A routing rule named a target the hub cannot use. Bring Back is refused for it.
  RULE_TARGET_UNUSABLE: 'Routing rule target cannot be used',
  // Every parked relay fax; the specific failure is the row's relayErrorCode.
  RELAY_FAX_FAILED: 'Relay fax failed',
  // Relay intake refused a rule-routed order after part of it may already have been relayed.
  RULE_RELAY_REFUSED: 'Relay refused a rule-routed order',
};

// Back on the filter now that the domain has four members (POM-6279). It was removed while
// STATE_NOT_LICENSED was the only one, because a one-member control can only show everything or
// nothing.
export const NON_ROUTABLE_REASON_OPTIONS = Object.entries(NON_ROUTABLE_REASON_LABEL).map(
  ([value, label]) => ({ label, value })
);

// NonRoutableRequestSource (POM-6279) — WHO parked the row, which is what decides the actions the
// server offers on it. Filtered as `sources`.
export const NON_ROUTABLE_SOURCE = {
  API_INTAKE: 'API_INTAKE',
  RELAY_FAX: 'RELAY_FAX',
  ESP_POM: 'ESP_POM',
};

export const NON_ROUTABLE_SOURCE_LABEL = {
  API_INTAKE: 'API Intake',
  RELAY_FAX: 'Relay Fax',
  ESP_POM: 'ESP / POM',
};

export const NON_ROUTABLE_SOURCE_OPTIONS = Object.entries(NON_ROUTABLE_SOURCE_LABEL).map(
  ([value, label]) => ({ label, value })
);

// The relay error code a RELAY_FAX row is parked for. The queue's relayErrorCode is the park row's
// last_error, which on a RELAY_FAX row is the relay order's error code. The codes are
// method-neutral, so the labels are too. Served as a String, not an enum, so anything outside this
// map renders raw.
export const RELAY_ERROR_LABEL = {
  RELAY_CONFIG_MISSING: 'Destination or sending pharmacy not configured',
  RELAY_DOCUMENT_FAILED: 'Document could not be built',
  RELAY_SEND_FAILED: 'Send refused (nothing transmitted)',
  RELAY_DELIVERY_FAILED: 'Delivery failed',
  RELAY_STATUS_UNKNOWN: 'Send status unknown',
};

// The fax service accepted a fax and later said it failed, or its outcome is unknown: the pharmacy
// may already hold it (RECONCILABLE_FAX_CODES in core).
export const FAX_MAY_BE_AT_PHARMACY_CODES = new Set([
  'RELAY_STATUS_UNKNOWN',
  'RELAY_DELIVERY_FAILED',
]);

/** How the fax dialogs name the pharmacy to phone. A business name, not PHI; null on some parks. */
export const relayFaxDestination = (item) => item?.destinationName || 'the destination pharmacy';

// Rendered on the NOT_PENDING refusal and on the fax detail view. Null on a CANCELLED row — the
// database enforces that, because cancelling is not a way of resolving — so the absence is itself
// information.
export const NON_ROUTABLE_RESOLUTION_TYPE_LABEL = {
  RETRY_ROUTED: 'Re-routed on retry',
  RELEASED_TO_POM: 'Released to POM',
  // RELAY_FAX rows. Both move the relay order to TRANSFERRED, which is what puts it on the Relay
  // tab.
  FAX_DELIVERED: 'Fax delivered',
  RECONCILED: 'Reconciled by phone',
};

/** The two fax resolutions after which the order shows on the Relay tab (relay order TRANSFERRED). */
export const RELAY_FAX_TRANSFERRED_RESOLUTIONS = new Set(['FAX_DELIVERED', 'RECONCILED']);

/**
 * NonRoutableRequestAction — the values the server serves per row as `availableActions`.
 *
 * THE MENU RENDERS THESE AND NEVER WORKS THEM OUT. Which actions a row offers depends on its
 * source, reason, status, relay error code, stale claim and whether relay orders may exist, and
 * the server decides it once (smartphos-core nonRoutableActionPolicy.ts) and refuses anything else
 * with NON_ROUTABLE_REQUEST_ACTION_NOT_ALLOWED. Re-deriving any of that here is how a button and a
 * refusal come to disagree.
 */
export const NON_ROUTABLE_ACTION = {
  RETRY_ROUTING: 'RETRY_ROUTING',
  BRING_BACK: 'BRING_BACK',
  CANCEL: 'CANCEL',
  RESEND_FAX: 'RESEND_FAX',
  RECONCILE_FAX: 'RECONCILE_FAX',
  CANCEL_FAX: 'CANCEL_FAX',
  MOVE_TO_POM: 'MOVE_TO_POM',
};

/**
 * How long a claim sits before the server calls it stale (isStaleClaim) and re-opens the row.
 * DISPLAY ONLY — used in the tooltip on an in-flight row so the operator knows when to come back.
 * Mirrors RELAY_FAX_STALE_CLAIM_MINUTES and ESP_POM_STALE_CLAIM_MINUTES in smartphos-core
 * nonRoutableActionPolicy.ts; the server's isStaleClaim stays the only thing that decides.
 */
export const RELAY_FAX_STALE_CLAIM_MINUTES = 90;
export const ESP_POM_STALE_CLAIM_MINUTES = 15;

/**
 * The statuses on which the request is OVER — no action will ever be offered and none is pending.
 *
 * Distinct from "offers no action", which a RESOLUTION_IN_PROGRESS row also usually does: that row
 * is mid-flight and its operator message is "wait" or an escalation, this one's is an explanation.
 * Only CANCELLED can appear in the queue; RESOLVED is reachable through the by-id and fax reads.
 */
export const NON_ROUTABLE_TERMINAL_STATUSES = new Set([
  NON_ROUTABLE_QUEUE_STATUS.CANCELLED,
  'RESOLVED',
]);

/**
 * Every member of the backend's `NonRoutableRequestsSortKey` enum, and nothing else.
 *
 * THE TABLE'S HEAD CELLS ARE CHECKED AGAINST THIS, because the failure mode is disproportionate:
 * a column marked sortable whose id is not in this enum makes the query fail GraphQL VALIDATION,
 * which carries no `NON_ROUTABLE_*` code, so the thunk falls back to `READ_FAILED` and the ENTIRE
 * queue reads "this queue did not load" until somebody reverts. One wrong flag on one column
 * takes the whole screen down, and the screen is the only place a parked prescription is visible.
 *
 * `parkedAt` is the DEFAULT and the only index-native ordering; the rest cost a server-side sort,
 * which is acceptable on request but must not be the default.
 */
export const NON_ROUTABLE_SORT_KEYS = new Set([
  'parkedAt',
  'lastUpdatedAt',
  'referenceId',
  'partnerId',
  'status',
  'nonRoutableReason',
  'attemptCount',
]);

/** True when a column id may be offered as sortable. */
export const isNonRoutableSortKey = (columnId) => NON_ROUTABLE_SORT_KEYS.has(columnId);
